import React from 'react';
import type { SchoolCode } from '../../types';
import {
  formatGradeForContext,
  getGradeColorForContext,
  usesLetterGrading,
} from '../../utils/letterGrading';
import { UserDto, GradeDto } from '../../api/axios';

interface GradeEntryTableProps {
  students: UserDto[];
  grades: GradeDto[];
  gradeValues: Record<string, { marks: string; grade: string }>;
  onGradeChange: (studentId: number, marks: string) => void;
  assignmentId?: number | null;
  loading?: boolean;
  school: SchoolCode;
  form: number;
}

export const GradeEntryTable: React.FC<GradeEntryTableProps> = ({
  students,
  grades,
  gradeValues,
  onGradeChange,
  assignmentId = null,
  loading = false,
  school,
  form,
}) => {
  const letterMode = usesLetterGrading(school, form);
  const pointsHeader = letterMode ? 'Grade' : 'Points';
  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
        <div>Loading students...</div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Students Found</div>
        <div>No students are enrolled in this class.</div>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        background: '#fff',
      }}
    >
      <div className="tableScroll">
        {/* Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '50px 2fr minmax(160px, 1fr) 90px minmax(180px, 1.2fr)',
            padding: '16px 20px',
            background: '#374151',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            minWidth: 760,
          }}
        >
          <div style={{ textAlign: 'center' }}>#</div>
          <div>Student Name</div>
          <div style={{ textAlign: 'center' }}>Current Grade</div>
          <div style={{ textAlign: 'center' }}>Marks</div>
          <div style={{ textAlign: 'center' }}>{pointsHeader}</div>
        </div>

        {/* Student Rows */}
        <div style={{ maxHeight: 500, overflowY: 'auto', minWidth: 760 }}>
          {students.map((student, idx) => {
          const key = `${student.id}`;
          const existingGrade = assignmentId
            ? grades.find((g) => g.student_id === student.id && g.assignment_id === assignmentId)
            : grades.find(
                (g) =>
                  g.student_id === student.id &&
                  (g.assignment_id === null || g.assignment_id === undefined)
              );

          const currentValue =
            gradeValues[key] ||
            (existingGrade
              ? { marks: existingGrade.marks.toString(), grade: existingGrade.grade }
              : { marks: '', grade: '' });

          const marksNum = parseInt(currentValue.marks, 10);
          const isValid = !isNaN(marksNum) && marksNum >= 0 && marksNum <= 100;
          const gradeColor = isValid ? getGradeColorForContext(school, form, marksNum) : '#999';
          const isEven = idx % 2 === 0;

          return (
            <div
              key={student.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '50px 2fr minmax(160px, 1fr) 90px minmax(180px, 1.2fr)',
                padding: '16px 20px',
                alignItems: 'center',
                background: isEven ? '#fafafa' : '#fff',
                borderBottom: '1px solid #e0e0e0',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f7ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isEven ? '#fafafa' : '#fff';
              }}
            >
              {/* Row Number */}
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#999',
                }}
              >
                {idx + 1}
              </div>

              {/* Student Name */}
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#333',
                  textTransform: 'capitalize',
                }}
              >
                {student.name.toLowerCase()}
              </div>

              {/* Current Grade Badge */}
              <div style={{ textAlign: 'center' }}>
                {existingGrade ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 12px',
                      background: getGradeColorForContext(school, form, existingGrade.marks),
                      color: '#fff',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      maxWidth: 200,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                    }}
                  >
                    {existingGrade.marks}% ·{' '}
                    {formatGradeForContext(school, form, existingGrade.marks, existingGrade.grade)}
                  </div>
                ) : (
                  <span style={{ color: '#999', fontSize: 13 }}>No grade</span>
                )}
              </div>

              {/* Marks Input */}
              <div style={{ textAlign: 'center' }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  style={{
                    width: '80px',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '2px solid #e0e0e0',
                    background: '#fff',
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                  placeholder="0-100"
                  value={currentValue.marks}
                  onChange={(e) => onGradeChange(student.id, e.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#007bff';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Letter Grade Display */}
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '6px 16px',
                    background: isValid ? gradeColor : '#f0f0f0',
                    color: isValid ? '#fff' : '#999',
                    borderRadius: 6,
                    fontSize: 18,
                    fontWeight: 700,
                    minWidth: 50,
                    boxShadow: isValid ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {isValid
                    ? formatGradeForContext(school, form, marksNum)
                    : '-'}
                </div>
              </div>
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};
